import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const frolundaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-frolunda",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Frölunda – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Frölunda. Vi hämtar möbler, vitvaror och skräp i Frölunda Torg, Tynnered, Järnbrott, Näset och Önnered. Fast pris, ofta samma dag.",
      h1: "Bortforsling Frölunda",
    },

    hero: {
      heading: "Bortforsling i Frölunda",
      intro:
        "Boka bortforsling i Frölunda – vi hämtar möbler, vitvaror och skrymmande föremål i hela sydvästra Göteborg, från höghusen kring Frölunda Torg, Tynnered och Järnbrott till villorna i Näset, Önnered och Fiskebäck. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Frölunda – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Frölunda",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Frölunda och körs till Högsbo återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Näset, Frölunda",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling kring Frölunda Torg",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Tynnered, Frölunda",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Järnbrott, Frölunda",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Önnered, Frölunda",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Frölunda – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Frölunda är en av sydvästra Göteborgs största stadsdelar, byggd som en modern satellitstad kring Frölunda Torg – ett av Skandinaviens största köpcentrum. Bebyggelsen är till stor del höghus från miljonprogrammets år i Järnbrott, Tynnered och Flatås, men sträcker sig också ut mot de havsnära villaområdena i Näset, Önnered, Fiskebäck och Långedrag. Den blandningen gör bortforsling till olika utmaningar: i höghusen handlar det ofta om trapphus, hiss och stora bostadsgårdar, medan det i kustvillorna snarare handlar om garage, förråd och ibland branta tomter. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. Här har Frölunda en fördel – Högsbo återvinningscentral ligger strax nordost om stadsdelen och tar emot det mesta. Göteborgs Stad bygger dessutom en ny, större kretsloppspark i Högsbo med fokus på återbruk. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att Dag Hammarskjöldsleden och Västerleden binder ihop Frölunda med centrala Göteborg, och spårvagnarna går ända ut till Frölunda Torg och Opaltorget, tar vi oss snabbt mellan områdena. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid både punkthusen kring Frölunda Torg och de smala villagatorna ner mot havet i Näset – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Frölunda",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Näset, Önnered, Fiskebäck eller Långedrag är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet kring Frölunda Torg, Tynnered, Järnbrott eller Flatås, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som göteborgare på Högsbo återvinningscentral.",
      additionalSections: [
        {
          heading: "Områden vi täcker i Frölunda och sydvästra Göteborg",
          content:
            "Vi utför bortforsling i hela Frölunda och sydvästra Göteborg – från de täta höghusområdena till de havsnära villakvarteren. Oavsett om du bor i en lägenhet vid Frölunda Torg, ett radhus i Flatås, en villa i Näset eller ett hus i Önnered kan vi vara på plats, ofta redan samma dag.\n\nKring Frölunda Torg och i Järnbrott har vi stor vana vid lägenhetshämtningar med hiss och trapphus i de stora flerbostadshusen, nära köpcentrum och spårvagnshållplatser. I Tynnered – med Grevegården, Kannebäck, Bratthammar och Opaltorget – möter vi liknande bebyggelse, och i Flatås och Kaverös en blandning av hus och lägenheter.\n\nVi är minst lika snabba i de västra och kustnära delarna: villaområdena i Näset, Önnered, Fiskebäck och Långedrag ut mot havet och Saltholmen, samt rekreationsområdet Ruddalen. Tack vare närheten till Dag Hammarskjöldsleden, Västerleden, Säröleden och spårvagnsnätet planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för något område.",
        },
        {
          heading: "Återvinning och miljöansvar i Frölunda",
          content:
            "En viktig del av vår bortforsling i Frölunda är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. I Göteborg sköts avfallet av Kretslopp och vatten, och de anläggningar vi använder mest för uppdrag i Frölunda är:\n\nHögsbo återvinningscentral – den närmaste stationen, strax nordost om Frölunda, där sorterat grovavfall, elavfall, förpackningar och farligt avfall tas emot. Göteborgs Stad bygger nu Kretsloppspark Högsbo, en ny och tre gånger större anläggning med reparationsverkstad, återbruksbutik och insamling för återbruk. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Frölunda med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Frölunda och sydvästra Göteborg, från höghusen kring Frölunda Torg och Tynnered till villatomterna i Näset och Önnered, och vet att Högsbo återvinningscentral ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen och når Frölunda smidigt via Dag Hammarskjöldsleden och Västerleden. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för något område i Frölunda, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Högsbo återvinningscentral och Kretslopp och vattens anläggningar. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Frölunda?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för något område i Frölunda.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Frölunda?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Frölunda?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och når Frölunda via Dag Hammarskjöldsleden och Västerleden, så vi tar oss snabbt mellan områdena. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Frölunda?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Frölunda?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i de stora flerbostadshusen kring Frölunda Torg, Tynnered, Järnbrott och Flatås. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Frölunda?",
        answer:
          "Ja, en stor del av våra uppdrag i Frölunda är villahämtningar i de kustnära områdena Näset, Önnered, Fiskebäck och Långedrag. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Frölunda försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Frölunda?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. För Frölunda är det oftast Högsbo återvinningscentral strax nordost om stadsdelen, som drivs av Kretslopp och vatten. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Frölunda på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Frölunda?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både de gemensamma förrådsutrymmena i höghusen kring Frölunda Torg och villaförråden i Näset och Önnered.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Frölunda?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet vid Frölunda Torg och ditt förråd på en annan adress i sydvästra Göteborg.",
      },
      {
        question: "Vilka områden i Frölunda täcker ni?",
        answer:
          "Vi utför bortforsling i hela Frölunda och sydvästra Göteborg – Frölunda Torg, Järnbrott, Tynnered (Grevegården, Kannebäck, Opaltorget), Flatås, Kaverös, Näset, Önnered, Fiskebäck och Långedrag. Inga extra avgifter för något område.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Frölunda?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som göteborgare på Högsbo återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Frölunda utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Frölunda i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Frölunda kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Frölunda idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Frölunda och sydvästra Göteborg.",
    },

    en: {
      slug: "junk-removal-frolunda",
      meta: {
        title: "Junk Removal Frölunda – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Frölunda. We pick up furniture, appliances and bulky items in Frölunda Torg, Tynnered, Järnbrott, Näset and Önnered. Fixed price, often same day.",
        h1: "Junk Removal Frölunda",
      },
      hero: {
        heading: "Junk Removal in Frölunda",
        intro:
          "Book junk removal in Frölunda – we collect furniture, appliances and bulky items across southwest Gothenburg, from the high-rises around Frölunda Torg, Tynnered and Järnbrott to the villas in Näset, Önnered and Fiskebäck. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Frölunda – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Frölunda",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Frölunda and driven to Högsbo recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Näset, Frölunda",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal around Frölunda Torg",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Tynnered, Frölunda",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Järnbrott, Frölunda",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Önnered, Frölunda",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Frölunda – fast, hassle-free and responsible",
        mainText:
          "Frölunda is one of southwest Gothenburg's largest districts, built as a modern satellite town around Frölunda Torg – one of Scandinavia's largest shopping centres. The housing is largely high-rises from the 'miljonprogram' era in Järnbrott, Tynnered and Flatås, but also stretches out towards the seaside villa areas of Näset, Önnered, Fiskebäck and Långedrag. That mix makes junk removal a different challenge: in the high-rises it is often stairwells, elevators and large residential courtyards, while in the coastal villas it is more about garages, storage rooms and sometimes steep plots. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. Here Frölunda has an advantage – Högsbo recycling centre is just northeast of the district and accepts most things. The City of Gothenburg is also building a new, larger circular-economy park in Högsbo with a focus on reuse. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause Dag Hammarskjöldsleden and Västerleden connect Frölunda with central Gothenburg, and the trams run all the way out to Frölunda Torg and Opaltorget, we move quickly between areas. This means we can often be on site the same day you book. Our workers are used to both the point blocks around Frölunda Torg and the narrow villa streets down towards the sea in Näset – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Frölunda",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Näset, Önnered, Fiskebäck or Långedrag, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment around Frölunda Torg, Tynnered, Järnbrott or Flatås, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Gothenburg resident you can drop those off for free at Högsbo recycling centre.",
        additionalSections: [
          {
            heading: "Areas we cover in Frölunda and southwest Gothenburg",
            content:
              "We perform junk removal across the whole of Frölunda and southwest Gothenburg – from the dense high-rise areas to the seaside villa neighbourhoods. Whether you live in an apartment at Frölunda Torg, a townhouse in Flatås, a villa in Näset or a house in Önnered, we can be on site, often the same day.\n\nAround Frölunda Torg and in Järnbrott we are highly experienced with apartment pickups via elevator and stairwell in the large apartment buildings, close to the shopping centre and tram stops. In Tynnered – with Grevegården, Kannebäck, Bratthammar and Opaltorget – we meet similar housing, and in Flatås and Kaverös a mix of houses and apartments.\n\nWe are just as quick in the western and coastal parts: the villa areas of Näset, Önnered, Fiskebäck and Långedrag out towards the sea and Saltholmen, and the Ruddalen recreation area. Thanks to the proximity of Dag Hammarskjöldsleden, Västerleden, Säröleden and the tram network, we always plan the route in advance so you don't have to wait. No extra charges for any area.",
          },
          {
            heading: "Recycling and environmental responsibility in Frölunda",
            content:
              "An important part of our junk removal in Frölunda is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. In Gothenburg, waste is handled by Kretslopp och vatten, and the facilities we use most for jobs in Frölunda are:\n\nHögsbo recycling centre – the closest station, just northeast of Frölunda, which receives sorted bulky waste, e-waste, packaging and hazardous waste. The City of Gothenburg is now building Kretsloppspark Högsbo, a new facility three times larger with a repair workshop, reuse shop and reuse collection. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Frölunda with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Frölunda and southwest Gothenburg, from the high-rises around Frölunda Torg and Tynnered to the villa plots of Näset and Önnered, and that Högsbo recycling centre is close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region and reach Frölunda smoothly via Dag Hammarskjöldsleden and Västerleden. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any area in Frölunda, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Högsbo recycling centre and Kretslopp och vatten's facilities. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Frölunda cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any area in Frölunda.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Frölunda?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Frölunda?",
          answer:
            "We have vans and staff across the Gothenburg region and reach Frölunda via Dag Hammarskjöldsleden and Västerleden, so we move quickly between areas. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Frölunda?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Frölunda?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the large apartment buildings around Frölunda Torg, Tynnered, Järnbrott and Flatås. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Frölunda?",
          answer:
            "Yes, a large share of our jobs in Frölunda are villa pickups in the coastal areas of Näset, Önnered, Fiskebäck and Långedrag. We reverse onto the driveway where possible and clear garages, basements and storage rooms. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Frölunda insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Frölunda?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. For Frölunda that is usually Högsbo recycling centre just northeast of the district, run by Kretslopp och vatten. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Frölunda on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Frölunda?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage rooms, attics and garages. We are used to both the shared storage rooms in the high-rises around Frölunda Torg and the villa storage rooms in Näset and Önnered.",
        },
        {
          question: "Can you make multiple stops during junk removal in Frölunda?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment at Frölunda Torg and a storage unit at another address in southwest Gothenburg, for example.",
        },
        {
          question: "Which areas in Frölunda do you cover?",
          answer:
            "We perform junk removal across all of Frölunda and southwest Gothenburg – Frölunda Torg, Järnbrott, Tynnered (Grevegården, Kannebäck, Opaltorget), Flatås, Kaverös, Näset, Önnered, Fiskebäck and Långedrag. No extra charges for any area.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Frölunda?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Gothenburg resident you can drop those off for free at Högsbo recycling centre.",
        },
        {
          question: "Which municipalities around Frölunda do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Frölunda in 3 easy steps",
        step2Text:
          "Our young workers in Frölunda come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Frölunda today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across Frölunda and southwest Gothenburg.",
      },
    },
  },
];
